import React, { useState } from "react";
import { PHARMACIST_CASES } from "../data/pharmacistData";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const CASES_PER_SESSION = 3;

// ── Medal helper ─────────────────────────────────────────────────────────────
function medal(pct) {
  if (pct >= 85) return { icon: "🥇", label: "Excellent", color: "#f59e0b" };
  if (pct >= 60) return { icon: "🥈", label: "Good",      color: "#94a3b8" };
  return               { icon: "🥉", label: "Keep practising", color: "#b45309" };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ClientCard({ client }) {
  return (
    <div className="flex items-start gap-4 bg-blue-50 border border-blue-200 rounded-2xl p-4">
      <div
        className="w-16 h-16 rounded-xl flex-shrink-0 border-2 border-white shadow overflow-hidden flex items-center justify-center"
        style={{ backgroundColor: client.color }}
      >
        <img
          src={client.sprite}
          alt={client.name}
          className="w-full h-full object-contain"
          style={{ imageRendering: "pixelated" }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-gray-900">{client.name}</span>
          <span className="text-xs text-gray-500">Age {client.age}</span>
          {client.flags.map(f => (
            <span key={f} className="bg-orange-100 text-orange-700 border border-orange-300 text-xs font-semibold px-2 py-0.5 rounded-full">
              ⚠️ {f}
            </span>
          ))}
        </div>
        <p className="text-sm text-gray-700 mt-1 leading-relaxed italic">"{client.complaint}"</p>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const PharmacistAdvisorGame = ({ onComplete, userData, saveUserData }) => {
  const [cases]  = useState(() => shuffle(PHARMACIST_CASES).slice(0, CASES_PER_SESSION));
  const [caseIdx, setCaseIdx] = useState(0);

  // per-case state
  const [phase, setPhase]                             = useState("pick-med");
  const [selectedMed, setSelectedMed]                 = useState(null);
  const [selectedCounselling, setSelectedCounselling] = useState(new Set());
  const [counsellingConfirmed, setCounsellingConfirmed] = useState(false);
  const [pendingResult, setPendingResult]             = useState(null);

  // accumulated results
  const [results, setResults] = useState([]);

  const current    = cases[caseIdx];
  const isLastCase = caseIdx === cases.length - 1;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleMedPick = (med) => {
    if (selectedMed) return; // already chosen
    setSelectedMed(med);
  };

  const handleProceedToCounselling = () => setPhase("counselling");

  const toggleCounselling = (id) => {
    setSelectedCounselling(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Step 1 — show inline feedback
  const handleConfirmCounselling = () => {
    const correctPoints = current.counsellingPoints.filter(p => p.correct);
    const correctHit    = correctPoints.filter(p => selectedCounselling.has(p.id));
    const wrongHit      = current.counsellingPoints.filter(p => !p.correct && selectedCounselling.has(p.id));
    const medScore      = selectedMed?.correct ? 50 : 0;
    const counselScore  = Math.max(0, correctHit.length * 12 - wrongHit.length * 8);
    const caseScore     = medScore + counselScore;
    const maxCaseScore  = 50 + correctPoints.length * 12;

    setPendingResult({
      caseId: current.id, clientName: current.client.name,
      medCorrect: !!selectedMed?.correct, medName: selectedMed?.name ?? "—",
      correctHit: correctHit.length, totalCorrect: correctPoints.length,
      wrongHit: wrongHit.length, caseScore, maxCaseScore,
    });
    setCounsellingConfirmed(true);
  };

  // Step 2 — advance after reading feedback
  const handleAdvance = () => {
    const updated = [...results, pendingResult];
    setResults(updated);

    if (isLastCase) {
      const total  = updated.reduce((s, r) => s + r.caseScore, 0);
      const maxTotal = updated.reduce((s, r) => s + r.maxCaseScore, 0);
      const pct    = Math.round((total / maxTotal) * 100);
      const coins  = Math.max(5, Math.floor(pct / 5));
      const xp     = Math.max(10, Math.floor(pct / 2));
      if (saveUserData && userData) {
        saveUserData({ ...userData, coins: (userData.coins || 0) + coins, xp: (userData.xp || 0) + xp });
      }
      setPhase("all-done");
    } else {
      setCaseIdx(i => i + 1);
      setPhase("pick-med");
      setSelectedMed(null);
      setSelectedCounselling(new Set());
      setCounsellingConfirmed(false);
      setPendingResult(null);
    }
  };

  // ── Render: all-done ──────────────────────────────────────────────────────

  if (phase === "all-done") {
    const total    = results.reduce((s, r) => s + r.caseScore, 0);
    const maxTotal = results.reduce((s, r) => s + r.maxCaseScore, 0);
    const pct      = Math.round((total / maxTotal) * 100);
    const m        = medal(pct);
    const coins    = Math.max(5, Math.floor(pct / 5));
    const xp       = Math.max(10, Math.floor(pct / 2));

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-lg">
          <div className="text-center mb-6">
            <div className="text-6xl mb-2">{m.icon}</div>
            <h2 className="text-2xl font-extrabold text-gray-900">{m.label}</h2>
            <p className="text-gray-500 text-sm mt-1">Session complete — {CASES_PER_SESSION} cases reviewed</p>
          </div>

          {/* Per-case breakdown */}
          <div className="space-y-3 mb-6">
            {results.map((r, i) => (
              <div key={r.caseId} className="bg-gray-50 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-800">Case {i + 1} — {r.clientName}</span>
                  <span className="text-sm font-bold" style={{ color: r.caseScore / r.maxCaseScore >= 0.6 ? "#16a34a" : "#dc2626" }}>
                    {r.caseScore}/{r.maxCaseScore}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1 flex gap-3 flex-wrap">
                  <span>{r.medCorrect ? "✅" : "❌"} Medication</span>
                  <span>💬 Counselling: {r.correctHit}/{r.totalCorrect} correct{r.wrongHit > 0 ? `, ${r.wrongHit} wrong` : ""}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 mb-6 text-center">
            <div className="text-3xl font-extrabold text-purple-700">{pct}%</div>
            <div className="text-sm text-purple-600 mt-1">
              {total} / {maxTotal} points
            </div>
            <div className="flex justify-center gap-6 mt-3">
              <div>
                <div className="font-bold text-yellow-600">+{coins} 🪙</div>
                <div className="text-xs text-gray-500">Coins</div>
              </div>
              <div>
                <div className="font-bold text-purple-600">+{xp} ⭐</div>
                <div className="text-xs text-gray-500">XP</div>
              </div>
            </div>
          </div>

          <button
            onClick={onComplete}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-500 text-white font-bold text-sm hover:opacity-90 transition-opacity"
          >
            Return to Pharmacy
          </button>
        </div>
      </div>
    );
  }

  // ── Render: pick-med ──────────────────────────────────────────────────────

  if (phase === "pick-med") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <button onClick={onComplete} className="text-sm text-gray-400 hover:text-gray-700">← Leave</button>
          <span className="font-bold text-gray-800 text-sm">💊 Pharmacist Advisor</span>
          <span className="text-xs text-gray-400">Case {caseIdx + 1} / {cases.length}</span>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 max-w-lg mx-auto w-full">
          {/* Progress */}
          <div className="flex gap-1">
            {cases.map((_, i) => (
              <div key={i} className={`flex-1 h-1.5 rounded-full ${i <= caseIdx ? "bg-purple-500" : "bg-gray-200"}`} />
            ))}
          </div>

          <ClientCard client={current.client} />

          <div>
            <p className="text-sm font-semibold text-gray-700 mb-3">Which medication would you recommend?</p>
            <div className="grid grid-cols-2 gap-3">
              {current.medications.map(med => {
                const isSelected = selectedMed?.id === med.id;
                const showResult = !!selectedMed;
                let borderColor = "border-gray-200";
                let bg = "bg-white";
                if (showResult && isSelected) {
                  borderColor = med.correct ? "border-green-400" : "border-red-400";
                  bg = med.correct ? "bg-green-50" : "bg-red-50";
                }
                return (
                  <button
                    key={med.id}
                    onClick={() => handleMedPick(med)}
                    disabled={!!selectedMed}
                    className={`text-left p-3 rounded-xl border-2 ${borderColor} ${bg} transition-all ${!selectedMed ? "hover:border-purple-400 hover:bg-purple-50 active:scale-95" : ""}`}
                  >
                    <div className="font-semibold text-sm text-gray-900">{med.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{med.category}</div>
                    {showResult && isSelected && (
                      <div className={`text-xs mt-1 font-semibold ${med.correct ? "text-green-700" : "text-red-700"}`}>
                        {med.correct ? "✅ Correct" : "❌ Incorrect"}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Feedback after picking */}
          {selectedMed && (
            <div className={`rounded-2xl p-4 border-2 ${selectedMed.correct ? "bg-green-50 border-green-300" : "bg-red-50 border-red-300"}`}>
              <p className="text-sm leading-relaxed text-gray-800">
                {selectedMed.correct ? current.correctFeedback : selectedMed.feedback}
              </p>
              <button
                onClick={handleProceedToCounselling}
                className="mt-3 w-full py-2.5 rounded-xl bg-purple-600 text-white font-bold text-sm hover:bg-purple-700 transition-colors"
              >
                Next: Counsel the patient →
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Render: counselling ───────────────────────────────────────────────────

  if (phase === "counselling") {
    const correctCount = current.counsellingPoints.filter(p => p.correct).length;
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <button onClick={onComplete} className="text-sm text-gray-400 hover:text-gray-700">← Leave</button>
          <span className="font-bold text-gray-800 text-sm">💊 Pharmacist Advisor</span>
          <span className="text-xs text-gray-400">Case {caseIdx + 1} / {cases.length}</span>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 max-w-lg mx-auto w-full">
          <ClientCard client={current.client} />

          <div>
            <p className="text-sm font-semibold text-gray-700 mb-1">
              {counsellingConfirmed
                ? "Here's how you did:"
                : `Select the counselling points you would give ${current.client.name}:`}
            </p>
            {!counsellingConfirmed && (
              <p className="text-xs text-gray-400 mb-3">
                Select all that apply — there are {correctCount} correct points.
              </p>
            )}
            <div className="space-y-2">
              {current.counsellingPoints.map(point => {
                const checked = selectedCounselling.has(point.id);

                // colour-coding after confirm
                let borderCls = "border-gray-200", bgCls = "bg-white", badge = null;
                if (counsellingConfirmed) {
                  if (checked && point.correct) {
                    borderCls = "border-green-400"; bgCls = "bg-green-50";
                    badge = <span className="ml-auto text-xs font-bold text-green-700 whitespace-nowrap">✅ Correct</span>;
                  } else if (checked && !point.correct) {
                    borderCls = "border-red-400"; bgCls = "bg-red-50";
                    badge = <span className="ml-auto text-xs font-bold text-red-700 whitespace-nowrap">❌ Incorrect</span>;
                  } else if (!checked && point.correct) {
                    borderCls = "border-amber-400"; bgCls = "bg-amber-50";
                    badge = <span className="ml-auto text-xs font-bold text-amber-700 whitespace-nowrap">⚠️ Missed</span>;
                  } else {
                    borderCls = "border-gray-200"; bgCls = "bg-gray-50";
                  }
                } else if (checked) {
                  borderCls = "border-purple-400"; bgCls = "bg-purple-50";
                }

                return (
                  <div
                    key={point.id}
                    onClick={() => !counsellingConfirmed && toggleCounselling(point.id)}
                    className={`w-full text-left p-3 rounded-xl border-2 flex items-start gap-3 transition-all ${borderCls} ${bgCls} ${!counsellingConfirmed ? "cursor-pointer hover:border-purple-200" : ""}`}
                  >
                    {!counsellingConfirmed && (
                      <div className={`w-5 h-5 rounded flex-shrink-0 mt-0.5 border-2 flex items-center justify-center ${
                        checked ? "bg-purple-500 border-purple-500" : "border-gray-300"
                      }`}>
                        {checked && <span className="text-white text-xs font-bold">✓</span>}
                      </div>
                    )}
                    <span className="text-sm text-gray-800 leading-snug flex-1">{point.text}</span>
                    {badge}
                  </div>
                );
              })}
            </div>
          </div>

          {!counsellingConfirmed ? (
            <button
              onClick={handleConfirmCounselling}
              disabled={selectedCounselling.size === 0}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-500 text-white font-bold text-sm disabled:opacity-40 hover:opacity-90 transition-opacity"
            >
              Confirm →
            </button>
          ) : (
            <button
              onClick={handleAdvance}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-500 text-white font-bold text-sm hover:opacity-90 transition-opacity"
            >
              {isLastCase ? "Finish session →" : "Next case →"}
            </button>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default PharmacistAdvisorGame;
